"use client";

import { c as _c } from "react/compiler-runtime";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";
import cn from "twsx";
const TooltipProvider = t0 => {
  const $ = _c(8);
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      delayDuration: t1,
      skipDelayDuration: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
    $[3] = t2;
  } else {
    props = $[1];
    t1 = $[2];
    t2 = $[3];
  }
  const delayDuration = t1 === undefined ? 400 : t1;
  const skipDelayDuration = t2 === undefined ? 300 : t2;
  let t3;
  if ($[4] !== delayDuration || $[5] !== props || $[6] !== skipDelayDuration) {
    t3 = <TooltipPrimitive.Provider delayDuration={delayDuration} skipDelayDuration={skipDelayDuration} {...props} />;
    $[4] = delayDuration;
    $[5] = props;
    $[6] = skipDelayDuration;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
TooltipProvider.displayName = TooltipPrimitive.Provider.displayName;
const TooltipPortalContainerContext = React.createContext<HTMLElement | null>(null);
const TooltipPortalContainerSetterContext = React.createContext<((container: HTMLElement | null) => void) | null>(null);
const resolveTooltipPortalContainer = (trigger: HTMLElement | null) => {
  if (!trigger) {
    return null;
  }
  const themedAncestor = trigger.closest("[data-theme]");
  return themedAncestor instanceof HTMLElement ? themedAncestor : null;
};
const assignRef = <T,>(ref: React.Ref<T> | undefined, value: T) => {
  if (typeof ref === "function") {
    ref(value);
    return;
  }
  if (ref && typeof ref !== "string") {
    (ref as React.MutableRefObject<T>).current = value;
  }
};
const Tooltip = t0 => {
  const $ = _c(9);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  const [portalContainer, setPortalContainer] = React.useState(null);
  let t1;
  if ($[3] !== children || $[4] !== props) {
    t1 = <TooltipPrimitive.Root {...props}>{children}</TooltipPrimitive.Root>;
    $[3] = children;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== portalContainer || $[7] !== t1) {
    t2 = <TooltipPortalContainerSetterContext.Provider value={setPortalContainer}><TooltipPortalContainerContext.Provider value={portalContainer}>{t1}</TooltipPortalContainerContext.Provider></TooltipPortalContainerSetterContext.Provider>;
    $[6] = portalContainer;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};
const TooltipTrigger = t0 => {
  const $ = _c(9);
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = ref;
  } else {
    props = $[1];
    ref = $[2];
  }
  const setPortalContainer = React.useContext(TooltipPortalContainerSetterContext);
  let t1;
  if ($[3] !== ref || $[4] !== setPortalContainer) {
    t1 = node => {
      setPortalContainer?.(resolveTooltipPortalContainer(node));
      assignRef(ref as React.Ref<HTMLButtonElement | null> | undefined, node);
    };
    $[3] = ref;
    $[4] = setPortalContainer;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const handleRef = t1;
  let t2;
  if ($[6] !== handleRef || $[7] !== props) {
    t2 = <TooltipPrimitive.Trigger ref={handleRef} {...props} />;
    $[6] = handleRef;
    $[7] = props;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};
const TooltipContent = t0 => {
  const $ = _c(15);
  let className;
  let props;
  let ref;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      sideOffset: t1,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
    $[4] = t1;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
    t1 = $[4];
  }
  const sideOffset = t1 === undefined ? 4 : t1;
  const portalContainer = React.useContext(TooltipPortalContainerContext);
  const t2 = portalContainer ?? undefined;
  let t3;
  if ($[5] !== className) {
    t3 = cn("motion-reduce-fade z-tooltip max-w-[240px] origin-[--radix-tooltip-content-transform-origin] overflow-hidden rounded-[var(--control-popover-radius)] border-[length:var(--surface-overlay-border-width)] border-surface-inverse-foreground/10 bg-surface-inverse px-[var(--surface-tooltip-content-padding-inline)] py-[var(--surface-tooltip-content-padding-block)] text-center text-xs text-surface-inverse-foreground shadow-comp-overlay duration-[var(--motion-overlay-duration)] ease-[var(--motion-overlay-timing)] animate-in fade-in-0 zoom-in-95 data-[state=instant-open]:animate-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2", className);
    $[5] = className;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== props || $[8] !== ref || $[9] !== sideOffset || $[10] !== t3) {
    t4 = <TooltipPrimitive.Content ref={ref} sideOffset={sideOffset} className={t3} {...props} />;
    $[7] = props;
    $[8] = ref;
    $[9] = sideOffset;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t2 || $[13] !== t4) {
    t5 = <TooltipPrimitive.Portal container={t2}>{t4}</TooltipPrimitive.Portal>;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};
TooltipContent.displayName = TooltipPrimitive.Content.displayName;
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;
export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };